import { useEffect, useMemo, useRef, useState } from "react";
import api, { money, apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, FileText, Download, Search, Layers, Sigma } from "lucide-react";
import { toast } from "sonner";

const STD_SERVICES = [
  "Professional Packing & Material Charges",
  "Transportation / Freight Charges",
  "Loading Charges",
  "Unloading Charges",
  "Unpacking Charges",
  "Handyman Services & Re-fixing Charges",
  "Warehousing Charges",
];

const blankBillTo = { name: "", company: "", address: "", city: "", state: "", gst_number: "", phone: "", email: "" };

export function InvoiceBuilderDialog({ open, onClose, customers = [], preselectCustomerId = "", invoice = null, onSaved }) {
  const [type, setType] = useState("tax");
  const [customerId, setCustomerId] = useState("");
  const [billTo, setBillTo] = useState(blankBillTo);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState(STD_SERVICES.map((d) => ({ description: d, amount: "" })));
  const [gstEnabled, setGstEnabled] = useState(true);
  const [gstMode, setGstMode] = useState("cgst_sgst");
  const [gstPercent, setGstPercent] = useState(18);
  const [meta, setMeta] = useState({ cost_centre: "", po_no: "", po_date: "", hsn_code: "", vehicle_no: "" });
  const [pulledFrom, setPulledFrom] = useState("");
  const [lrData, setLrData] = useState({});
  const [activeLrTab, setActiveLrTab] = useState("");
  const mergedSig = useRef("");
  const [lrs, setLrs] = useState([]);
  const [selectedLrs, setSelectedLrs] = useState([]);
  const [showAllLrs, setShowAllLrs] = useState(false);
  const [lrQuery, setLrQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (invoice) {
      setType(invoice.invoice_type || "tax");
      setCustomerId(invoice.customer_id || "");
      setBillTo({ ...blankBillTo, ...(invoice.bill_to || {}) });
      setInvoiceDate(invoice.invoice_date || new Date().toISOString().slice(0, 10));
      setNotes(invoice.notes || "");
      setItems((invoice.line_items || []).map((li) => ({ description: li.description || "", amount: li.amount != null ? String(li.amount) : "" })));
      setGstEnabled(invoice.gst_enabled !== false);
      setGstMode(invoice.gst_mode || "cgst_sgst");
      setGstPercent(invoice.gst_percent != null ? invoice.gst_percent : 18);
      setMeta({ cost_centre: invoice.cost_centre || "", po_no: invoice.po_no || "", po_date: invoice.po_date || "", hsn_code: invoice.hsn_code || "", vehicle_no: invoice.vehicle_no || "" });
      setPulledFrom("");
      setSelectedLrs((invoice.lrs || []).map((x) => ({ lead_id: x.lead_id, lr_id: x.lr_id, lr_number: x.lr_number, order_number: x.order_number })));
      setShowAllLrs(true); setLrQuery("");
      setLrData({}); mergedSig.current = "editing"; setActiveLrTab("");
    } else {
      const pc = preselectCustomerId ? customers.find((x) => x.id === preselectCustomerId) : null;
      setType("tax"); setInvoiceDate(new Date().toISOString().slice(0, 10)); setNotes("");
      setItems(STD_SERVICES.map((d) => ({ description: d, amount: "" })));
      setGstEnabled(true); setGstMode("cgst_sgst"); setGstPercent(18);
      setMeta({ cost_centre: "", po_no: "", po_date: "", hsn_code: "", vehicle_no: "" });
      setPulledFrom("");
      setSelectedLrs([]); setShowAllLrs(false); setLrQuery("");
      setLrData({}); mergedSig.current = ""; setActiveLrTab("");
      setCustomerId(preselectCustomerId || "");
      setBillTo(pc ? { name: pc.name || "", company: pc.company || "", address: pc.address || "", city: pc.city || "", state: pc.state || "", gst_number: pc.gst_number || "", phone: pc.phone || "", email: pc.email || "" } : blankBillTo);
    }
  }, [open, preselectCustomerId, invoice]); // eslint-disable-line

  const onCustomerChange = (id) => {
    setCustomerId(id);
    if (!id) { setBillTo(blankBillTo); return; }
    const c = customers.find((x) => x.id === id);
    if (c) setBillTo({ name: c.name || "", company: c.company || "", address: c.address || "", city: c.city || "", state: c.state || "", gst_number: c.gst_number || "", phone: c.phone || "", email: c.email || "" });
  };

  useEffect(() => {
    if (!open) return;
    api.get(`/lrs/available${customerId ? `?customer_id=${customerId}` : ""}`).then((r) => setLrs(r.data)).catch(() => {});
  }, [open, customerId]);

  const visibleLrs = useMemo(() => {
    const q = lrQuery.trim().toLowerCase();
    return lrs.filter((l) => {
      if (!showAllLrs && customerId && !l.matches_customer) return false;
      if (q && !(l.lr_number || "").toLowerCase().includes(q) && !(l.order_number || "").toLowerCase().includes(q) && !(l.customer_name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [lrs, showAllLrs, customerId, lrQuery]);

  const toggleLr = (l) => {
    setSelectedLrs((s) => s.some((x) => x.lr_id === l.lr_id)
      ? s.filter((x) => x.lr_id !== l.lr_id)
      : [...s, { lead_id: l.lead_id, lr_id: l.lr_id, lr_number: l.lr_number, order_number: l.order_number }]);
  };

  // Fetch quotation prefill for every selected LR (internal per-LR preview data)
  useEffect(() => {
    if (!open) return;
    const missing = selectedLrs.filter((s) => !lrData[s.lr_id]);
    if (!missing.length) return;
    Promise.all(missing.map((s) => api.get(`/leads/${s.lead_id}/invoice-prefill?lr_id=${s.lr_id}`).then((r) => ({ id: s.lr_id, data: r.data })).catch(() => null)))
      .then((res) => setLrData((prev) => { const n = { ...prev }; res.forEach((r) => { if (r) n[r.id] = r.data; }); return n; }));
  }, [selectedLrs, open]); // eslint-disable-line

  useEffect(() => {
    if (selectedLrs.length && (!activeLrTab || !selectedLrs.some((s) => s.lr_id === activeLrTab))) setActiveLrTab(selectedLrs[0].lr_id);
  }, [selectedLrs, activeLrTab]);

  const lrSubtotal = (lrId) => (lrData[lrId]?.line_items || []).reduce((a, x) => a + (parseFloat(x.amount) || 0), 0);

  const mergeItemsFrom = (sel) => {
    const acc = {}; const order = [];
    sel.forEach((s) => (lrData[s.lr_id]?.line_items || []).forEach((li) => {
      const key = (li.description || "").trim().toLowerCase() || `__blank_${order.length}`;
      if (!(key in acc)) { acc[key] = { description: li.description || "", amount: 0 }; order.push(key); }
      acc[key].amount += parseFloat(li.amount) || 0;
    }));
    return order.map((k) => ({ description: acc[k].description, amount: String(acc[k].amount) }));
  };

  const doMergeAll = (silent) => {
    const merged = mergeItemsFrom(selectedLrs);
    if (merged.length) setItems(merged);
    const first = lrData[selectedLrs[0]?.lr_id];
    if (first) setMeta((m) => ({ cost_centre: m.cost_centre, po_no: m.po_no || first.po_no || "", po_date: m.po_date || first.po_date || "", hsn_code: m.hsn_code || first.hsn_code || "", vehicle_no: m.vehicle_no || first.vehicle_no || "" }));
    if (!silent) toast.success(`Merged ${selectedLrs.length} LR(s) → ${merged.length} consolidated line item(s)`);
  };

  // Auto-aggregate whenever the selected-LR set changes and all their data is loaded (skip when editing)
  useEffect(() => {
    if (!open || invoice) return;
    if (!selectedLrs.length) return;
    if (!selectedLrs.every((s) => lrData[s.lr_id])) return;
    const sig = selectedLrs.map((s) => s.lr_id).sort().join("|");
    if (mergedSig.current === sig) return;
    mergedSig.current = sig;
    doMergeAll(true);
    const first = lrData[selectedLrs[0].lr_id];
    if (first?.lr_bill_to) setBillTo((b) => ({ ...b, name: b.name && b.name !== (b.company || "") ? b.name : first.lr_bill_to, company: first.lr_bill_to }));
    else if (!customerId && first?.bill_to && !billTo.name) setBillTo((b) => ({ ...b, ...first.bill_to }));
  }, [lrData, selectedLrs, open, invoice]); // eslint-disable-line

  const setItem = (i, patch) => setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const addItem = () => setItems((arr) => [...arr, { description: "", amount: "" }]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const subtotal = useMemo(() => items.reduce((a, x) => a + (parseFloat(x.amount) || 0), 0), [items]);
  const tax = type === "tax" && gstEnabled ? subtotal * (parseFloat(gstPercent) || 0) / 100 : 0;
  const total = subtotal + tax;

  const buildBody = () => ({
    invoice_type: type,
    customer_id: customerId,
    bill_to: billTo,
    invoice_date: invoiceDate,
    lrs: selectedLrs,
    notes,
    line_items: items.filter((x) => (x.description || "").trim() || parseFloat(x.amount) > 0).map((x) => ({ description: x.description, amount: parseFloat(x.amount) || 0 })),
    gst_enabled: gstEnabled,
    gst_mode: gstMode,
    gst_percent: parseFloat(gstPercent) || 0,
    cost_centre: meta.cost_centre,
    po_no: meta.po_no,
    po_date: meta.po_date,
    hsn_code: meta.hsn_code,
    vehicle_no: meta.vehicle_no,
  });

  const [includeCarrier, setIncludeCarrier] = useState(true);

  const save = async (download) => {
    if (!billTo.name.trim()) return toast.error("Bill To name is required");
    setSaving(true);
    try {
      let data;
      if (invoice) {
        ({ data } = await api.put(`/invoices/${invoice.id}`, buildBody()));
        toast.success(`Invoice ${data.serial} updated`);
      } else {
        ({ data } = await api.post("/invoices", buildBody()));
        toast.success(`Invoice ${data.serial} created`);
      }
      if (download) {
        const res = await api.get(`/invoices/${data.id}/pdf?include_carrier_risk=${includeCarrier}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = `${data.serial.replace(/\//g, "-")}-invoice.pdf`; a.click();
        URL.revokeObjectURL(url);
      }
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const bt = (k, v) => setBillTo((b) => ({ ...b, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="invoice-builder-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> {invoice ? `Edit Invoice ${invoice.serial}` : "Create Invoice"}</DialogTitle></DialogHeader>

        <div className="space-y-5 py-2">
          {/* Type toggle */}
          <div className="grid grid-cols-2 gap-2">
            {[{ k: "tax", t: "Tax Invoice", s: "With GST breakdown" }, { k: "bog", t: "Bill of Goods", s: "No tax" }].map((o) => (
              <button key={o.k} onClick={() => setType(o.k)} data-testid={`inv-type-${o.k}`}
                className={`border p-3 text-left transition-colors ${type === o.k ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}>
                <div className="font-bold text-sm">{o.t}</div>
                <div className="text-[11px] text-muted-foreground">{o.s}</div>
              </button>
            ))}
          </div>

          {/* Bill To */}
          <div className="border border-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Bill To (Customer)</div>
              <select className="rounded-none h-8 text-xs bg-background border border-border px-2" value={customerId} onChange={(e) => onCustomerChange(e.target.value)} data-testid="inv-customer-select">
                <option value="">— Manual entry —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ""}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input className="rounded-none h-9" placeholder="Customer name *" value={billTo.name} onChange={(e) => bt("name", e.target.value)} data-testid="inv-billto-name" />
              <Input className="rounded-none h-9" placeholder="Company" value={billTo.company} onChange={(e) => bt("company", e.target.value)} data-testid="inv-billto-company" />
              <Input className="rounded-none h-9 sm:col-span-2" placeholder="Address" value={billTo.address} onChange={(e) => bt("address", e.target.value)} data-testid="inv-billto-address" />
              <Input className="rounded-none h-9" placeholder="City" value={billTo.city} onChange={(e) => bt("city", e.target.value)} />
              <Input className="rounded-none h-9" placeholder="State" value={billTo.state} onChange={(e) => bt("state", e.target.value)} />
              <Input className="rounded-none h-9" placeholder="Phone" value={billTo.phone} onChange={(e) => bt("phone", e.target.value)} />
              {type === "tax" && <Input className="rounded-none h-9 font-mono-num" placeholder="GSTIN" value={billTo.gst_number} onChange={(e) => bt("gst_number", e.target.value)} data-testid="inv-billto-gst" />}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Invoice Date</Label><Input type="date" className="rounded-none h-9 mt-1" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="inv-date" /></div>
              <div className="flex items-end"><div className="text-xs text-muted-foreground">Invoice Value: <span className="font-mono-num font-bold text-foreground">{money(total)}</span></div></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 border-t border-border pt-3">
              <div><Label className="text-xs">Cost Centre</Label><Input className="rounded-none h-9 mt-1" value={meta.cost_centre} onChange={(e) => setMeta((m) => ({ ...m, cost_centre: e.target.value }))} data-testid="inv-cost-centre" /></div>
              <div><Label className="text-xs">PO No.</Label><Input className="rounded-none h-9 mt-1" value={meta.po_no} onChange={(e) => setMeta((m) => ({ ...m, po_no: e.target.value }))} data-testid="inv-po-no" /></div>
              <div><Label className="text-xs">PO Date</Label><Input type="date" className="rounded-none h-9 mt-1" value={meta.po_date} onChange={(e) => setMeta((m) => ({ ...m, po_date: e.target.value }))} data-testid="inv-po-date" /></div>
              <div><Label className="text-xs">HSN Code</Label><Input className="rounded-none h-9 mt-1 font-mono-num" value={meta.hsn_code} onChange={(e) => setMeta((m) => ({ ...m, hsn_code: e.target.value }))} data-testid="inv-hsn" /></div>
              <div><Label className="text-xs">Vehicle No.</Label><Input className="rounded-none h-9 mt-1 font-mono-num" value={meta.vehicle_no} onChange={(e) => setMeta((m) => ({ ...m, vehicle_no: e.target.value }))} data-testid="inv-vehicle-no" /></div>
            </div>
          </div>

          {/* LR selection */}
          <div className="border border-border p-4 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Bill Against LR(s) — {selectedLrs.length} selected</div>
              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                <input type="checkbox" checked={showAllLrs} onChange={(e) => setShowAllLrs(e.target.checked)} data-testid="inv-show-all-lrs" /> Show all LRs
              </label>
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input className="rounded-none h-8 pl-7 text-xs" placeholder="Search LR / order / customer…" value={lrQuery} onChange={(e) => setLrQuery(e.target.value)} data-testid="inv-lr-search" />
            </div>
            <div className="max-h-40 overflow-y-auto border border-border divide-y divide-border" data-testid="inv-lr-list">
              {visibleLrs.length === 0 && <div className="p-3 text-xs text-muted-foreground text-center">No LRs {customerId && !showAllLrs ? "for this customer — tick 'Show all LRs'" : "found"}.</div>}
              {visibleLrs.map((l) => {
                const on = selectedLrs.some((x) => x.lr_id === l.lr_id);
                return (
                  <label key={l.lr_id} className={`flex items-center gap-2 p-2 text-xs cursor-pointer hover:bg-muted ${on ? "bg-primary/5" : ""}`} data-testid={`inv-lr-${l.lr_number}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleLr(l)} />
                    <span className="font-mono-num font-bold w-24 truncate">{l.lr_number || "—"}</span>
                    <span className="text-muted-foreground w-24 truncate">{l.order_number}</span>
                    <span className="flex-1 truncate">{l.customer_name}{l.matches_customer ? "" : ""}</span>
                    <span className="text-muted-foreground truncate hidden sm:block">{l.route}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Per-LR quotation preview (internal, tabbed) */}
          {selectedLrs.length > 0 && (
            <div className="border border-border p-4 space-y-3" data-testid="lr-preview">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> LR Quotation Preview</div>
                <Button type="button" size="sm" variant="outline" className="rounded-none text-xs gap-1 font-bold uppercase tracking-wide" onClick={() => doMergeAll(false)} disabled={!selectedLrs.every((s) => lrData[s.lr_id])} data-testid="inv-merge-all"><Sigma className="h-3.5 w-3.5" /> Merge All ({selectedLrs.length})</Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Compare each LR's quoted rate. These per-LR tabs are for internal preparation only and are not printed on the invoice PDF. Grand total of merged services is shown below.</p>
              <Tabs value={activeLrTab} onValueChange={setActiveLrTab}>
                <TabsList className="rounded-none bg-muted flex-wrap h-auto justify-start">
                  {selectedLrs.map((s, i) => (
                    <TabsTrigger key={s.lr_id} value={s.lr_id} className="rounded-none text-xs" data-testid={`lr-tab-${i}`}>LR {i + 1}{s.lr_number ? ` · ${s.lr_number}` : ""}</TabsTrigger>
                  ))}
                </TabsList>
                {selectedLrs.map((s, i) => {
                  const d = lrData[s.lr_id];
                  const rows = d?.line_items || [];
                  return (
                    <TabsContent key={s.lr_id} value={s.lr_id} className="pt-3">
                      {!d ? <div className="text-xs text-muted-foreground py-4 text-center">Loading LR details…</div> : (
                        <div className="space-y-2" data-testid={`lr-tab-content-${i}`}>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                            <div><div className="text-muted-foreground uppercase">Order</div><div className="font-mono-num">{s.order_number || "—"}</div></div>
                            <div><div className="text-muted-foreground uppercase">Vehicle</div><div className="font-mono-num">{d.vehicle_no || "—"}</div></div>
                            <div><div className="text-muted-foreground uppercase">PO No.</div><div className="font-mono-num">{d.po_no || "—"}</div></div>
                            <div><div className="text-muted-foreground uppercase">HSN</div><div className="font-mono-num">{d.hsn_code || "—"}</div></div>
                          </div>
                          <div className="border border-border divide-y divide-border">
                            {rows.length === 0 && <div className="p-2 text-xs text-muted-foreground text-center">No quotation lines for this LR.</div>}
                            {rows.map((li, j) => (
                              <div key={j} className="flex justify-between px-2 py-1 text-xs"><span className="truncate pr-2">{li.description}</span><span className="font-mono-num">{money(li.amount)}</span></div>
                            ))}
                          </div>
                          <div className="flex justify-between text-sm font-bold border-t border-border pt-1"><span>Quoted Rate (LR {i + 1})</span><span className="font-mono-num text-primary" data-testid={`lr-quoted-${i}`}>{money(lrSubtotal(s.lr_id))}</span></div>
                        </div>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-xs">Notes / Special Instructions (printed above the line items)</Label>
            <Textarea rows={2} className="rounded-none mt-1" placeholder="e.g. Payment due within 15 days; goods insured in transit…" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inv-notes" />
          </div>

          {/* Line items */}
          <div className="border border-border">
            <div className="grid grid-cols-[1fr_140px_36px] gap-2 bg-muted px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              <span>{selectedLrs.length > 1 ? "Consolidated Services (all LRs merged)" : "Description"}</span><span className="text-right">Amount (₹)</span><span />
            </div>
            <div className="divide-y divide-border">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_140px_36px] gap-2 px-3 py-1.5 items-center" data-testid={`inv-item-${i}`}>
                  <Input className="rounded-none h-8 text-sm" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} placeholder="Service / description" />
                  <Input type="number" min="0" step="0.01" className="rounded-none h-8 text-sm font-mono-num text-right" value={it.amount} onChange={(e) => setItem(i, { amount: e.target.value })} placeholder="0.00" data-testid={`inv-item-amount-${i}`} />
                  <button onClick={() => removeItem(i)} className="h-8 w-8 grid place-items-center text-muted-foreground hover:text-destructive" data-testid={`inv-item-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="p-2 border-t border-border">
              <Button variant="outline" size="sm" className="rounded-none text-xs gap-1" onClick={addItem} data-testid="inv-add-line"><Plus className="h-3 w-3" /> Add Custom Line</Button>
            </div>
          </div>

          {/* GST controls */}
          {type === "tax" && (
            <div className="border border-border p-4 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-1.5 text-sm font-bold cursor-pointer"><input type="checkbox" checked={gstEnabled} onChange={(e) => setGstEnabled(e.target.checked)} data-testid="inv-gst-toggle" /> Apply GST</label>
              {gstEnabled && <>
                <div className="flex items-center gap-1.5 text-sm"><span>Rate %</span><Input type="number" min="0" step="0.5" className="rounded-none h-8 w-20 font-mono-num" value={gstPercent} onChange={(e) => setGstPercent(e.target.value)} data-testid="inv-gst-percent" /></div>
                <div className="flex items-center gap-2 text-xs">
                  {[{ k: "cgst_sgst", t: "CGST + SGST" }, { k: "igst", t: "IGST" }].map((o) => (
                    <button key={o.k} onClick={() => setGstMode(o.k)} data-testid={`inv-gst-mode-${o.k}`} className={`px-3 py-1.5 border font-bold uppercase tracking-wide ${gstMode === o.k ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>{o.t}</button>
                  ))}
                </div>
              </>}
            </div>
          )}

          {/* Totals */}
          <div className="border border-border p-4 space-y-1 text-sm" data-testid="inv-totals">
            <div className="flex justify-between"><span className="text-muted-foreground">Sub Total</span><span className="font-mono-num">{money(subtotal)}</span></div>
            {type === "tax" && gstEnabled && <div className="flex justify-between"><span className="text-muted-foreground">GST @ {gstPercent}% {gstMode === "igst" ? "(IGST)" : "(CGST+SGST)"}</span><span className="font-mono-num">{money(tax)}</span></div>}
            <div className="flex justify-between border-t border-border pt-1 font-bold text-base"><span>Net Total</span><span className="font-mono-num text-primary">{money(total)}</span></div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between items-center">
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none mr-auto" title="Append each LR's Carrier Risk Receipt after the invoice">
            <input type="checkbox" checked={includeCarrier} onChange={(e) => setIncludeCarrier(e.target.checked)} data-testid="inv-include-carrier" /> Include Carrier Risk Receipt
          </label>
          <div className="flex gap-2">
            <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="outline" className="rounded-none gap-1" onClick={() => save(false)} disabled={saving} data-testid="inv-save">{invoice ? "Update Invoice" : "Save Invoice"}</Button>
            <Button className="rounded-none gap-1 font-bold uppercase tracking-wide" onClick={() => save(true)} disabled={saving} data-testid="inv-save-download"><Download className="h-4 w-4" /> {invoice ? "Update & Download" : "Save & Download"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
